import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { GzToastNotificationWebComponent } from '../../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../../ui/gzToastNotification/type';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { FontSizes, FontWeights, TemplateState } from '../../../../types/common/types';
import { ImgPosition } from '../../../../productTrials/common/gzTrialProductItem/type';
import { GzLandingPageConfig } from '../../../../productTrials/common/gzLandingPage/types';
import {
  getMdrOnboardingTranslations,
  getMspLandingPageConfigForAddonFlag,
  getMspTranslationsForCountdownByReason
} from '../../../../productTrials/common/gzLandingPage/view/gzLandingPageConfig/gz-landing-page-config';
import {
  COOLDOWN_DAYS,
  getGenerateReportButtonItem,
  getReconfigureButtonItemTextsWhenActive,
  getReconfigureButtonItemTextsWhenInCooldown,
  getTrialCards,
  MspInProductTrialCannotBeStartedReason,
  MspInProductTrialId,
  MspInProductTrialState,
  MspIptButtonItems,
  translateProductIdReference,
  TrialCard,
} from '../types/msp-in-product-trials-types';
import { CountdownTranslations } from "../../../../ui/gzCountDown/types";
import { Rights } from "@nirvana/shared/constants";
import { MspLandingPageWebComponent } from "../../../../productTrials/msp/mspLandingPage";
import { ConfirmationWindowItem } from "../../../../productTrials/msp/mspLandingPage/types";

export class CompanyMspInProductTrialsTabView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;
    $trialCardsSection: HTMLElement;
    $gzLandingPage: MspLandingPageWebComponent;
  };
  public reconfigureConfirmationWindow: ConfirmationWindowItem;
  public isEdrPaid: boolean;
  public companySelectedCountry: string;
  private availableTrialStatuses: Map<MspInProductTrialId, MspInProductTrialState>;
  private currentTrial: MspInProductTrialId;
  public companySelectedIndustry: number;
  public companySelectedState: string | null;

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $trialCardsSection: this.shadow.querySelector('#trialCards'),
      $gzLandingPage: this.shadow.querySelector('#msp-in-product-trials-landing-page'),
    };

    this.setTranslatedStrings();
  }

  setTranslatedStrings() {
    this.dom.$gzLandingPage.texts.title = this.dependencies.translateFn('Capabilities & Benefits');
    this.dom.$gzLandingPage.texts.faqTitle = this.dependencies.translateFn('FAQ');
  }

  public discardFormChanges() {
  }

  public resetTrialCards(): void {
    this.availableTrialStatuses = new Map<MspInProductTrialId, MspInProductTrialState>();
  }

  public addTrialCard(trial: MspInProductTrialId, state: MspInProductTrialState): void {
    this.availableTrialStatuses.set(trial, state);
  }

  buildSuccessView() {
    this.buildTab();
  }

  public buildTab(): void {
    this.setElementClass(this.dom.$gzLandingPage, 'hidden');
    this.removeElementClass(this.dom.$trialCardsSection, 'hidden');
    if (this.availableTrialStatuses.size === 0) {
      this.dom.$trialCardsSection.innerHTML = this.getEmptyStateTabHtml();
      return;
    }

    const imageAlignment = this.availableTrialStatuses.size === 1 ? ImgPosition.LEFT : ImgPosition.TOP;

    this.dom.$trialCardsSection.innerHTML = ``;

    getTrialCards().forEach((trial, trialId) => {
      const trialStatus = this.availableTrialStatuses.get(trialId);
      if (trialStatus) {
        this.dom.$trialCardsSection.innerHTML += this.getTrialCardHtml(trialId, trial, imageAlignment, trialStatus);
      }
    });

    getTrialCards().forEach((trial, trialId) => {
      if (this.availableTrialStatuses.get(trialId)) {
        const learnMoreButton = this.shadow.querySelector('#' + trial.buttonId);
        if (learnMoreButton) {
          learnMoreButton.addEventListener('click', this.showLandingPage.bind(this, trialId));
        }
      }
    });
  }

  buildErrorView(error: Error) {
    this.removeLoadingState();
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: error.message,
      severity: ToastSeverity.ERROR,
    });
    toggleVisibility(this.dom.$wrapper, false);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private getInCooldownCountdownTime(date: number): number {
    return date + COOLDOWN_DAYS * 24 * 60 * 60;
  }

  private getTrialCardHtml(trialId: MspInProductTrialId, trial: TrialCard, imageAlignment, trialStatus: MspInProductTrialState): string {
    let countDownTime;
    if (trialStatus.trialState === MspInProductTrialCannotBeStartedReason.InCooldown && trialStatus.endDate) {
      countDownTime = this.getInCooldownCountdownTime(trialStatus.endDate);
    }
    if (trialStatus.trialState === MspInProductTrialCannotBeStartedReason.TrialAlreadyInProgressForCurrentProduct && trialStatus.endDate) {
      countDownTime = trialStatus.endDate;
    }
    const isMdrOnboarding = this.isMdrOnboarding(trialId, trialStatus);
    return `<gz-trial-product-item
              productTitle="${trial.title}"
              description="${trial.description}"
              imageAlignment=${imageAlignment}
              imageSrc="${trial.imageSrc}"
              countDownTime="${countDownTime}"
              mspStatus="${trialStatus.trialState}"
              ${isMdrOnboarding ? "isMspMdrOnboarding" : ""}>
                <gz-button id=${trial.buttonId}>${trial.buttonText}</gz-button>
            </gz-trial-product-item>`;
  }

  private getEmptyStateTabHtml(): string {
    return `<gz-image-repo state="start">
      <gz-title class="empty-title" fontSize="${FontSizes.HEADING_4}" fontWeight="${FontWeights.MEDIUM}">
        ${this.dependencies.translateFn('No trial available')}
      </gz-title>
      <gz-text class="empty-description" fontSize="${FontSizes.PARAGRAPH_12}">
        ${this.dependencies.translateFn('Check the release notes and this page for any future changes.')}
      </gz-text>
    </gz-image-repo>`;
  }

  private setCountdownDate(trialState: MspInProductTrialCannotBeStartedReason, date: number | null): void {
    if (date === null) {
      date = 0;
    }
    if (trialState === MspInProductTrialCannotBeStartedReason.InCooldown) {
      this.dom.$gzLandingPage.dateToTimestamp = this.getInCooldownCountdownTime(date);
    } else {
      this.dom.$gzLandingPage.dateToTimestamp = date;
    }
  }

  private isMdrOnboarding(trialId: MspInProductTrialId, mspIptState: MspInProductTrialState) {
    return trialId === MspInProductTrialId.MdrTrial
           && mspIptState.trialState === MspInProductTrialCannotBeStartedReason.TrialAlreadyInProgressForCurrentProduct
           && !mspIptState.endDate;
  }

  private showLandingPage(trialId: MspInProductTrialId): void {
    this.currentTrial = trialId;

    this.setElementClass(this.dom.$trialCardsSection, 'hidden');
    this.removeElementClass(this.dom.$gzLandingPage, 'hidden');

    const gzLandingPageConfig: GzLandingPageConfig = getMspLandingPageConfigForAddonFlag(trialId);

    this.dom.$gzLandingPage.productItem = gzLandingPageConfig.productItem;
    this.dom.$gzLandingPage.hasBackButton = true;

    this.dom.$gzLandingPage.cardItems = gzLandingPageConfig.cardItems;
    this.dom.$gzLandingPage.hasOutlineAroundAccordionItems = gzLandingPageConfig.hasOutlineAroundAccordionItems ?? true;
    this.dom.$gzLandingPage.accordionItems = gzLandingPageConfig.accordionItems;
    this.dom.$gzLandingPage.accordionItemsFaq = gzLandingPageConfig.accordionItemsFaq ?? [];

    const mspIptState = this.availableTrialStatuses.get(trialId);

    if (mspIptState) {
      this.setCountdownDate(mspIptState.trialState, mspIptState.endDate);

      this.dom.$gzLandingPage.setCannotBeStartedReason(mspIptState.trialState);

      const gzLandingPageCountdownTranslation: CountdownTranslations | undefined =
        getMspTranslationsForCountdownByReason(mspIptState.trialState);
      this.dom.$gzLandingPage.setCountdownTranslations(gzLandingPageCountdownTranslation);

      if (this.isMdrOnboarding(trialId, mspIptState)) {
        this.dom.$gzLandingPage.setMdrOnboarding(true, getMdrOnboardingTranslations().mdrOnboardingMessage, getMdrOnboardingTranslations().mdrOnboardingTooltip);
      } else {
        this.dom.$gzLandingPage.setMdrOnboarding(false);
      }

      const buttonWithActionItems = {} as MspIptButtonItems;

      if (mspIptState.trialState === MspInProductTrialCannotBeStartedReason.TrialAlreadyInProgressForCurrentProduct &&
          Application.Main.hasAllRights([Rights.RIGHT_MANAGE_INVENTORY])) {
        buttonWithActionItems.reconfigureTaskButton = getReconfigureButtonItemTextsWhenActive()[trialId].buttonItem;
        this.reconfigureConfirmationWindow = getReconfigureButtonItemTextsWhenActive()[trialId].confirmationWindowItem;
      }

      if (mspIptState.trialState === MspInProductTrialCannotBeStartedReason.InCooldown &&
          Application.Main.hasAllRights([Rights.RIGHT_MANAGE_INVENTORY])) {
        // exception for MDR trial, when company has paid EDR
        if (!(trialId === MspInProductTrialId.MdrTrial && this.isEdrPaid)) {
          buttonWithActionItems.reconfigureTaskButton = getReconfigureButtonItemTextsWhenInCooldown()[trialId].buttonItem;
          this.reconfigureConfirmationWindow = getReconfigureButtonItemTextsWhenInCooldown()[trialId].confirmationWindowItem;
        }
      }

      if (mspIptState.trialState === MspInProductTrialCannotBeStartedReason.InCooldown ||
          mspIptState.trialState === MspInProductTrialCannotBeStartedReason.TrialAlreadyInProgressForCurrentProduct) {
        buttonWithActionItems.generateReportButton = getGenerateReportButtonItem()[trialId];
      }

      this.dom.$gzLandingPage.buttonItems = buttonWithActionItems;

      this.trackPage(translateProductIdReference(trialId));
    }
  }

  public updateLandingPage(): void {
    this.showLandingPage(this.currentTrial);
  }

  public trackPage(subSubSubSection: string) {
    analytics.trackPageView({
      parentName: 'companies',
      name: 'product trial hub',
      tabName: subSubSubSection,
    });
  }
}
